import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import notificacionService from '../services/notificacionService';
import gmailService from '../services/gmailService';
import outlookService from '../services/outlookService';
import useBreakpoint from '../hooks/useBreakpoint';
import PageHeader from '../components/ui/PageHeader';

const ORIGEN_ICONO = {
    GMAIL:   { icon: 'bi-google',    color: '#EA4335', label: 'Gmail' },
    OUTLOOK: { icon: 'bi-microsoft', color: '#0078D4', label: 'Outlook' },
};

const formatFecha = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// Extrae el remitente del campo "mensaje": "📧 Nuevo mail de: X — Asunto: Y"
const parsearMensaje = (mensaje) => {
    if (!mensaje) return { remitente: '', asunto: '' };
    const match = mensaje.match(/Nuevo mail de:\s*(.+?)\s*—\s*Asunto:\s*(.+)/);
    if (match) return { remitente: match[1].trim(), asunto: match[2].trim() };
    return { remitente: '', asunto: mensaje };
};

// ── Panel de filtros reutilizable (para autoservicio) ──────────────────────
function PanelFiltros({ servicio, idSuscripcion, onError }) {
    const [filtros, setFiltros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tipo, setTipo] = useState('REMITENTE');
    const [valor, setValor] = useState('');
    const [agregando, setAgregando] = useState(false);

    useEffect(() => {
        if (!idSuscripcion) return;
        setLoading(true);
        servicio.getMisFiltros(idSuscripcion)
            .then(res => setFiltros(res.data))
            .catch(() => onError('Error al cargar filtros'))
            .finally(() => setLoading(false));
    }, [servicio, idSuscripcion, onError]);

    const agregar = async () => {
        if (!valor.trim()) return;
        setAgregando(true);
        try {
            const res = await servicio.agregarMiFiltro(idSuscripcion, tipo, valor.trim());
            setFiltros(prev => [...prev, res.data]);
            setValor('');
        } catch (err) {
            onError(err.response?.data?.error || 'Error al agregar filtro');
        } finally {
            setAgregando(false);
        }
    };

    const eliminar = async (idFiltro) => {
        try {
            await servicio.eliminarMiFiltro(idSuscripcion, idFiltro);
            setFiltros(prev => prev.filter(f => f.idFiltro !== idFiltro));
        } catch {
            onError('Error al eliminar filtro');
        }
    };

    if (loading) return <p className="text-muted small mt-2">Cargando filtros...</p>;

    return (
        <div className="mt-2">
            <div className="d-flex flex-wrap gap-1 mb-2">
                {filtros.length === 0 ? (
                    <span className="text-muted small fst-italic">Sin filtros — se notifican todos los mails</span>
                ) : filtros.map(f => (
                    <span key={f.idFiltro} className="badge d-flex align-items-center gap-1"
                        style={{
                            background: f.tipo === 'REMITENTE' ? '#fff7ed' : '#f5f3ff',
                            color: f.tipo === 'REMITENTE' ? '#9a3412' : '#4c1d95',
                            border: `1px solid ${f.tipo === 'REMITENTE' ? '#fed7aa' : '#ddd6fe'}`,
                            fontSize: '0.78rem', padding: '3px 8px', borderRadius: 6,
                        }}>
                        <span style={{ opacity: 0.6, fontSize: '0.7rem' }}>{f.tipo === 'REMITENTE' ? '@' : '#'}</span>
                        {f.valor}
                        <button onClick={() => eliminar(f.idFiltro)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginLeft: 2, lineHeight: 1 }}>
                            <i className="bi bi-x" style={{ fontSize: '0.75rem' }} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="d-flex gap-2">
                <select className="form-select form-select-sm" value={tipo} onChange={e => setTipo(e.target.value)} style={{ width: 120, borderRadius: 6 }}>
                    <option value="REMITENTE">Remitente</option>
                    <option value="ASUNTO">Asunto</option>
                </select>
                <input type="text" className="form-control form-control-sm" value={valor} onChange={e => setValor(e.target.value)}
                    placeholder={tipo === 'REMITENTE' ? 'ej: @ejemplo.com' : 'ej: expediente'}
                    onKeyDown={e => { if (e.key === 'Enter') agregar(); }}
                    style={{ borderRadius: 6 }} />
                <button className="btn btn-sm btn-outline-warning fw-semibold" onClick={agregar}
                    disabled={agregando || !valor.trim()} style={{ borderRadius: 6, whiteSpace: 'nowrap' }}>
                    {agregando ? <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> : '+ Agregar'}
                </button>
            </div>
        </div>
    );
}

// ── Tab "Mis conexiones" ───────────────────────────────────────────────────
function MisConexiones() {
    const [gmailSuscripciones, setGmailSuscripciones] = useState([]);
    const [outlookSuscripciones, setOutlookSuscripciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [conectandoGmail, setConectandoGmail] = useState(false);
    const [conectandoOutlook, setConectandoOutlook] = useState(false);
    const [expandido, setExpandido] = useState({});

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const [gRes, oRes] = await Promise.all([
                gmailService.getMisSuscripciones().catch(() => ({ data: [] })),
                outlookService.getMisSuscripciones().catch(() => ({ data: [] })),
            ]);
            setGmailSuscripciones(Array.isArray(gRes.data) ? gRes.data : []);
            setOutlookSuscripciones(Array.isArray(oRes.data) ? oRes.data : []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('gmailConectado') === 'true') {
            setError('');
            cargar();
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get('outlookConectado') === 'true') {
            setError('');
            cargar();
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [cargar]);

    const conectarGmail = async () => {
        setConectandoGmail(true);
        try {
            const res = await gmailService.getMiAuthUrl();
            window.location.href = res.data.url;
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo generar el enlace de autorización de Gmail');
            setConectandoGmail(false);
        }
    };

    const conectarOutlook = async () => {
        setConectandoOutlook(true);
        try {
            const res = await outlookService.getMiAuthUrl();
            window.location.href = res.data.url;
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo generar el enlace de autorización de Outlook');
            setConectandoOutlook(false);
        }
    };

    const toggleExpandido = (key) => setExpandido(prev => ({ ...prev, [key]: !prev[key] }));

    if (loading) return <p className="text-muted p-4">Cargando conexiones...</p>;

    return (
        <div style={{ maxWidth: 700, padding: '1.5rem 0' }}>
            {error && (
                <div className="alert alert-danger mb-4" style={{ borderRadius: 10 }}>{error}</div>
            )}

            {/* Gmail */}
            <div className="card shadow-sm mb-4" style={{ borderRadius: 12, border: 'none' }}>
                <div className="card-header d-flex align-items-center justify-content-between"
                    style={{ background: 'var(--primary)', borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem' }}>
                    <div className="d-flex align-items-center gap-2 text-white">
                        <i className="bi bi-google fs-5" />
                        <span className="fw-semibold">Gmail</span>
                    </div>
                    <button className="btn btn-sm btn-light fw-semibold" onClick={conectarGmail} disabled={conectandoGmail}
                        style={{ borderRadius: 8, fontSize: '0.8rem' }}>
                        {conectandoGmail
                            ? <><i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }} />Redirigiendo...</>
                            : <><i className="bi bi-plus-circle me-1" />Conectar Gmail personal</>
                        }
                    </button>
                </div>
                <div className="card-body" style={{ padding: '1rem 1.25rem' }}>
                    <div className="rounded-3 p-3 mb-3 d-flex align-items-start gap-2"
                        style={{ background: '#fffbeb', border: '1px solid #fde68a', fontSize: '0.82rem', color: '#92400e' }}>
                        <i className="bi bi-exclamation-triangle-fill text-warning mt-1 flex-shrink-0" />
                        <span>
                            <strong>API en modo desarrollo:</strong> para poder conectar tu cuenta de Gmail, primero necesitás que habilitemos tu dirección.
                            Envianos un <a href="/herramientas/feedback" style={{ color: '#92400e', fontWeight: 600 }}>mensaje de feedback</a> con
                            el mail de Gmail que querés vincular y te lo habilitamos a la brevedad. Luego podrás usar el botón de arriba para conectarlo.
                        </span>
                    </div>
                    {gmailSuscripciones.length === 0 ? (
                        <p className="text-muted small mb-0">No estás suscripto a ninguna cuenta de Gmail. Hacé clic en <strong>Conectar Gmail personal</strong> para vincular tu cuenta.</p>
                    ) : gmailSuscripciones.map(s => (
                        <div key={s.idSuscripcion} className="mb-3 rounded-3 p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="fw-semibold" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{s.emailCuenta}</span>
                                    {s.ultimaConsulta && (
                                        <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Última consulta: {formatFecha(s.ultimaConsulta)}</div>
                                    )}
                                </div>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleExpandido('g' + s.idSuscripcion)}
                                    style={{ borderRadius: 8, fontSize: '0.75rem' }}>
                                    <i className={`bi bi-chevron-${expandido['g' + s.idSuscripcion] ? 'up' : 'down'}`} /> Filtros
                                </button>
                            </div>
                            {expandido['g' + s.idSuscripcion] && (
                                <PanelFiltros
                                    servicio={gmailService}
                                    idSuscripcion={s.idSuscripcion}
                                    onError={setError}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Outlook */}
            <div className="card shadow-sm" style={{ borderRadius: 12, border: 'none' }}>
                <div className="card-header d-flex align-items-center justify-content-between"
                    style={{ background: 'var(--info-text)', borderRadius: '12px 12px 0 0', padding: '1rem 1.25rem' }}>
                    <div className="d-flex align-items-center gap-2 text-white">
                        <i className="bi bi-microsoft fs-5" />
                        <span className="fw-semibold">Outlook / Hotmail</span>
                    </div>
                    <button className="btn btn-sm btn-light fw-semibold" onClick={conectarOutlook} disabled={conectandoOutlook}
                        style={{ borderRadius: 8, fontSize: '0.8rem' }}>
                        {conectandoOutlook
                            ? <><i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }} />Redirigiendo...</>
                            : <><i className="bi bi-plus-circle me-1" />Conectar Outlook personal</>
                        }
                    </button>
                </div>
                <div className="card-body" style={{ padding: '1rem 1.25rem' }}>
                    {outlookSuscripciones.length === 0 ? (
                        <p className="text-muted small mb-0">No estás suscripto a ninguna cuenta de Outlook. Hacé clic en <strong>Conectar Outlook personal</strong> para vincular tu cuenta.</p>
                    ) : outlookSuscripciones.map(s => (
                        <div key={s.idSuscripcion} className="mb-3 rounded-3 p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="fw-semibold" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{s.emailCuenta}</span>
                                    {s.ultimaConsulta && (
                                        <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Última consulta: {formatFecha(s.ultimaConsulta)}</div>
                                    )}
                                </div>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleExpandido('o' + s.idSuscripcion)}
                                    style={{ borderRadius: 8, fontSize: '0.75rem' }}>
                                    <i className={`bi bi-chevron-${expandido['o' + s.idSuscripcion] ? 'up' : 'down'}`} /> Filtros
                                </button>
                            </div>
                            {expandido['o' + s.idSuscripcion] && (
                                <PanelFiltros
                                    servicio={outlookService}
                                    idSuscripcion={s.idSuscripcion}
                                    onError={setError}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

// ── Página principal ───────────────────────────────────────────────────────
export default function CasillaCorreosPage() {
    const { esMobile } = useBreakpoint();
    const [searchParams] = useSearchParams();
    const mailIdParam = searchParams.get('mailId');
    const [tab, setTab] = useState('correos');

    const [emails, setEmails] = useState([]);
    const [loadingEmails, setLoadingEmails] = useState(true);
    const [emailSeleccionado, setEmailSeleccionado] = useState(null);

    const itemsRefs = useRef({});
    const lastAutoSelectedMailId = useRef(null);

    const cargarEmails = useCallback(async () => {
        try {
            const res = await notificacionService.getCorreos();
            const todos = Array.isArray(res?.data) ? res.data : [];
            todos.sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));
            setEmails(todos);
        } catch {
            setEmails([]);
        } finally {
            setLoadingEmails(false);
        }
    }, []);

    useEffect(() => { cargarEmails(); }, [cargarEmails]);

    // Si viene mailId en la URL, seleccionarlo y scrollear (solo una vez por mailId)
    useEffect(() => {
        if (!mailIdParam || emails.length === 0) return;
        const id = parseInt(mailIdParam, 10);
        if (lastAutoSelectedMailId.current === id) return;
        const encontrado = emails.find(e => e.id === id);
        if (encontrado) {
            lastAutoSelectedMailId.current = id;
            setEmailSeleccionado(encontrado);
            setTab('correos');
            setTimeout(() => {
                itemsRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
        }
    }, [mailIdParam, emails]);

    const handleSeleccionar = async (item) => {
        setEmailSeleccionado(item);
        if (!item.leido) {
            try {
                await notificacionService.marcarComoLeidaUsuario(item.id);
                setEmails(prev => prev.map(e => e.id === item.id ? { ...e, leido: true } : e));
            } catch { /* no crítico */ }
        }
    };

    const noLeidosCount = emails.filter(e => !e.leido).length;

    // Contenido de la lista y del detalle, extraídos para reusarlos tal cual entre el layout
    // de escritorio (lado a lado) y el de mobile (una sola columna a la vez) sin duplicar el
    // markup — ver el layout de cada uno más abajo, en el return.
    const contenidoLista = (
        <div style={{ overflowY: 'auto', flex: 1 }}>
            {loadingEmails ? (
                <p className="text-muted p-3 mb-0">Cargando...</p>
            ) : emails.length === 0 ? (
                <div className="text-center py-5 px-3">
                    <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: '#cbd5e1' }} />
                    <p className="mt-3 text-muted small">No hay correos recibidos</p>
                </div>
            ) : (
                emails.map(item => {
                    const seleccionado = emailSeleccionado?.id === item.id;
                    const { remitente, asunto } = parsearMensaje(item.mensaje);
                    const meta = ORIGEN_ICONO[item.origen] || ORIGEN_ICONO.GMAIL;
                    return (
                        <div
                            key={item.id}
                            ref={el => { itemsRefs.current[item.id] = el; }}
                            onClick={() => handleSeleccionar(item)}
                            style={{
                                padding: '0.85rem 1rem',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                borderLeft: seleccionado ? '4px solid #3B82F6' : item.leido ? '4px solid transparent' : '4px solid #ffc107',
                                background: seleccionado ? '#eff6ff' : item.leido ? '#fff' : '#fffbeb',
                                transition: 'background 0.15s',
                            }}
                        >
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '0.8rem', flexShrink: 0 }} />
                                <span
                                    className={item.leido ? 'text-muted' : 'fw-semibold'}
                                    style={{ fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}
                                    title={remitente}
                                >
                                    {remitente || '(sin remitente)'}
                                </span>
                                {!item.leido && (
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ffc107', flexShrink: 0 }} />
                                )}
                            </div>
                            <div
                                style={{ fontSize: '0.85rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: item.leido ? 400 : 600 }}
                                title={asunto}
                            >
                                {asunto || '(sin asunto)'}
                            </div>
                            <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: 2 }}>
                                {formatFecha(item.fechaProgramada)}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    const contenidoDetalle = !emailSeleccionado ? (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted py-5">
            <i className="bi bi-envelope-open" style={{ fontSize: '3rem', color: '#cbd5e1' }} />
            <p className="mt-3 mb-0">Seleccioná un correo para leerlo</p>
        </div>
    ) : (() => {
        const { remitente, asunto } = parsearMensaje(emailSeleccionado.mensaje);
        const meta = ORIGEN_ICONO[emailSeleccionado.origen] || ORIGEN_ICONO.GMAIL;
        return (
            <>
                <div className="card-header" style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0', background: '#fff', borderRadius: esMobile ? 0 : '12px 12px 0 0' }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '1rem' }} title={meta.label} />
                        <h6 className="mb-0 fw-bold" style={{ color: '#1e293b', fontSize: '1rem' }}>
                            {asunto || '(sin asunto)'}
                        </h6>
                    </div>
                    <div className="d-flex gap-3" style={{ fontSize: '0.82rem', color: '#64748b' }}>
                        <span><i className="bi bi-person me-1" />{remitente || '(sin remitente)'}</span>
                        <span><i className="bi bi-clock me-1" />{formatFecha(emailSeleccionado.fechaProgramada)}</span>
                    </div>
                </div>
                <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
                    {emailSeleccionado.cuerpoTexto ? (
                        <>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'inherit',
                                fontSize: '0.9rem',
                                color: '#334155',
                                lineHeight: 1.6,
                                margin: 0,
                            }}>
                                {emailSeleccionado.cuerpoTexto}
                            </pre>
                            <div className="mt-4 rounded-3 p-3 d-flex align-items-start gap-2"
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#64748b' }}>
                                <i className="bi bi-info-circle-fill text-primary mt-1 flex-shrink-0" />
                                <span>
                                    Si el mail incluye imágenes o archivos adjuntos, accedé directamente desde{' '}
                                    <strong>{meta.label}</strong> para verlos.
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                            <i className="bi bi-file-earmark-x" style={{ fontSize: '2.5rem', color: '#cbd5e1' }} />
                            <p className="mt-3 mb-1">No hay texto disponible para este correo</p>
                            <p className="small">Para ver el contenido completo, accedé desde <strong>{meta.label}</strong>.</p>
                        </div>
                    )}
                </div>
            </>
        );
    })();

    return (
        <div className="area-herramientas">
            <div style={{ padding: 'clamp(1rem, 3vw, 1.5rem)', maxWidth: 1100, margin: '0 auto' }}>
                <PageHeader
                    variante="plano"
                    icono="inbox-fill"
                    titulo="Bandeja de Entrada"
                    descripcion="Correos recibidos a través de cuentas sincronizadas"
                />

                {/* Tabs */}
                <div className="d-flex gap-2 mb-4 align-items-center">
                <button
                    className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                    onClick={() => setTab('correos')}
                    style={{
                        borderRadius: 10, padding: '0.5rem 1.2rem',
                        background: tab === 'correos' ? 'var(--primary)' : '#f1f5f9',
                        color: tab === 'correos' ? '#fff' : '#64748b',
                        border: 'none', transition: 'all 0.2s',
                    }}
                >
                    <i className="bi bi-envelope-fill" />
                    Correos
                    {noLeidosCount > 0 && (
                        <span className="badge rounded-pill bg-warning text-dark" style={{ fontSize: '0.7rem', padding: '2px 7px' }}>
                            {noLeidosCount}
                        </span>
                    )}
                </button>
                <button
                    className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                    onClick={() => setTab('conexiones')}
                    style={{
                        borderRadius: 10, padding: '0.5rem 1.2rem',
                        background: tab === 'conexiones' ? 'var(--primary)' : '#f1f5f9',
                        color: tab === 'conexiones' ? '#fff' : '#64748b',
                        border: 'none', transition: 'all 0.2s',
                    }}
                >
                    <i className="bi bi-link-45deg" />
                    Mis conexiones
                </button>
            </div>

            {/* ── Tab correos ── */}
            {tab === 'correos' && (esMobile ? (
                // Mobile: una sola columna a la vez — lista, o detalle con volver. El layout
                // lado a lado de escritorio (ver más abajo) fuerza la lista a 340px fijos y
                // deja el detalle en el resto, que en una pantalla angosta queda una tira de
                // ~90px inutilizable — acá directamente no compiten por el mismo ancho.
                <div className="card shadow-sm" style={{ borderRadius: 12, border: 'none', minHeight: 'calc(100vh - 260px)', display: 'flex', flexDirection: 'column' }}>
                    {!emailSeleccionado ? (
                        <>
                            <div className="card-header fw-semibold"
                                style={{ background: 'var(--primary)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '0.9rem 1rem' }}>
                                <i className="bi bi-inbox me-2" />
                                Bandeja de entrada
                                {noLeidosCount > 0 && (
                                    <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.75rem' }}>
                                        {noLeidosCount} sin leer
                                    </span>
                                )}
                            </div>
                            {contenidoLista}
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="mob-tocable"
                                onClick={() => setEmailSeleccionado(null)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, border: 'none',
                                    borderBottom: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155',
                                    padding: '0.7rem 1rem', fontSize: '0.85rem', fontWeight: 600, textAlign: 'left',
                                }}
                            >
                                <i className="bi bi-arrow-left" aria-hidden="true" />
                                Volver a la bandeja
                            </button>
                            {contenidoDetalle}
                        </>
                    )}
                </div>
            ) : (
                <div className="d-flex gap-3" style={{ alignItems: 'flex-start' }}>
                    {/* Lista */}
                    <div className="card shadow-sm flex-shrink-0"
                        style={{ width: 340, borderRadius: 12, border: 'none', maxHeight: 'calc(100vh - 180px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div className="card-header fw-semibold"
                            style={{ background: 'var(--primary)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '0.9rem 1rem' }}>
                            <i className="bi bi-inbox me-2" />
                            Bandeja de entrada
                            {noLeidosCount > 0 && (
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.75rem' }}>
                                    {noLeidosCount} sin leer
                                </span>
                            )}
                        </div>
                        {contenidoLista}
                    </div>

                    {/* Detalle */}
                    <div className="card shadow-sm flex-grow-1"
                        style={{ borderRadius: 12, border: 'none', maxHeight: 'calc(100vh - 180px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {contenidoDetalle}
                    </div>
                </div>
            ))}

            {/* ── Tab conexiones ── */}
            {tab === 'conexiones' && <MisConexiones />}

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
}
